import { asLinkAttrs, asText } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';

import { cn } from '@april9au/react-common/utils';

import type { KeyTextField, RichTextField } from '@prismicio/client';
import type { JSXMapSerializer } from '@prismicio/react';
import { Media } from '@/components/ui/media';
import { useStrings } from '@/i18n/use-strings';

/**
 * Prismic text in the chat.
 *
 * Empty fields render the editor-facing `[empty]` hint the current app shows,
 * rather than collapsing to nothing: a blank bubble in a scripted conversation
 * looks like a bug, and staff need to see which slice is unfinished.
 */

/**
 * True when a rich text field has any renderable content.
 *
 * A picture counts. Half the course's questions are authored as an image with
 * no words at all — "tap the sign that means marine stingers" is three photos —
 * and reading those as empty put the editor-facing `[empty]` hint in front of
 * the learner.
 */
export function hasText(field: RichTextField | string | null | undefined): boolean {
  if (field === null || field === undefined) {
    return false;
  }

  if (typeof field === 'string') {
    return field.trim() !== '';
  }

  return asText(field).trim() !== '' || field.some((node) => node.type === 'image');
}

export type RichTextProps = {
  field: RichTextField | string | null | undefined;
  className?: string;
};

/**
 * Renders a rich text field, or plain text where a slice authors it that way.
 * Links, emphasis, lists and pictures all survive.
 */
export function RichText({ field, className }: RichTextProps) {
  if (!hasText(field)) {
    return <EmptyHint className={className} />;
  }

  if (typeof field === 'string') {
    return <p className={className}>{field}</p>;
  }

  return (
    <div
      className={cn(
        '[&_a]:text-surf-blue [&_a]:underline [&_a]:underline-offset-2 [&_p+p]:mt-2 [&_strong]:font-bold',
        '[&_li]:ms-4 [&_li]:list-disc [&_ul]:mt-2 [&_ul]:space-y-1',
        className,
      )}
    >
      <PrismicRichText field={field} components={chatSerialiser} />
    </div>
  );
}

/**
 * React components rather than Prismic's HTML string, for one reason: an image
 * node serialised to HTML is a bare `<img>` with no width or height, so half
 * the questions in the course grew a box after their bytes arrived and pushed
 * the thread down. `Media` reserves the exact box from the asset's own
 * dimensions and fades the picture in over a tint.
 */
const chatSerialiser: JSXMapSerializer = {
  image: ({ node, key }) => (
    <Media
      key={key}
      src={node.url}
      alt={node.alt ?? ''}
      width={node.dimensions.width}
      height={node.dimensions.height}
      placeholder="var(--color-surf-gray)"
      className="mt-2 h-auto w-full max-w-56 rounded-xl bg-white object-contain first:mt-0"
    />
  ),

  // `asLinkAttrs` is the same resolver Prismic's own HTML serialiser uses, so a
  // link opens in a new tab only where the editor asked for it (or where the
  // field is an external one) rather than every link doing it.
  hyperlink: ({ node, children, key }) => {
    const { href, target, rel } = asLinkAttrs(node.data);

    return href === undefined || href.trim() === '' ? (
      <span key={key}>{children}</span>
    ) : (
      <a key={key} href={href} target={target} rel={rel}>
        {children}
      </a>
    );
  },
};

/** A Key Text field, with the same `[empty]` treatment. */
export function PlainText({ field, className }: { field: KeyTextField; className?: string }) {
  const text = field?.trim();

  return text === undefined || text === '' ? (
    <EmptyHint className={className} />
  ) : (
    <span className={className}>{text}</span>
  );
}

/** The editor-facing hint. Muted and italic so nobody mistakes it for copy. */
export function EmptyHint({ className }: { className?: string }) {
  const { chat } = useStrings();

  return <span className={cn('text-chat-ink-muted italic', className)}>{chat.chat_empty}</span>;
}

/** A Key Text field as a string, with the hint as its fallback. */
export function plainTextOr(field: KeyTextField, fallback: string): string {
  const text = field?.trim();

  return text === undefined || text === '' ? fallback : text;
}
